import { map, of } from 'rxjs';
import { readEntitiesByIDs, watchEntitiesByIDs, readAllEntities, watchAllEntities } from '../server/entity-reads.js';
import { type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { scopedCollection } from '../server/scoped-collection.js';
import { getCurrentUserServerOrFail, type ServerCachedUser } from '@adornis/bh-util/util.js';
import { Payment } from '@adornis/payments/db/payments.js';
import { BHPayment } from '../entities/BHPayment.js';
import { mongo } from '../server/mongo.js';
import { getAllowedUsers } from './users.js';

export const getUserProductsForWhichAPaymentExists = registerQuery({
  operationName: 'getUserProductsForWhichAPaymentExists',
  params: [],
  type: () => [String],
  resolve: () => {
    return async () => {
      const user = await getCurrentUserServerOrFail();
      const paymentCollection = await scopedCollection(BHPayment._class);
      const payments = (await paymentCollection
        .find({ 'userReference.userID': user._id }, { projection: { 'products.product._id': 1 } })
        .toArray()) as BHPayment[];
      return payments.reduce<string[]>((acc, payment) => {
        const products = payment.products.map(cp => cp.product._id);
        return [...acc, ...products];
      }, []);
    };
  },
});

export async function getAllowedPayments(user: ServerCachedUser) {
  const userIDs = await getAllowedUsers(user);
  const aggregation = [
    {
      $match: {
        'userReference.userID': {
          $in: userIDs,
        },
      },
    },
    { $group: { _id: null, ids: { $push: '$_id' } } },
  ];
  const paymentCollection = mongo().collection(Payment._collectionName);
  const result = await paymentCollection.aggregate(aggregation).toArray();
  return result.at(0)?.ids ?? [];
}

export const getBHPaymentsByIDs = registerQuery({
  operationName: 'getBHPaymentsByIDs',
  type: () => [BHPayment],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<BHPayment>>(gqlFields: SelSet) =>
      readEntitiesByIDs(BHPayment, ids, gqlFields),
});

export const subscribeBHPaymentsByIDs = registerSubscription({
  operationName: 'subscribeBHPaymentsByIDs',
  type: () => [BHPayment],
  params: [{ name: 'ids', type: () => [ID] }],
  resolve:
    (ids: string[]) =>
    <SelSet extends BaseQLSelectionSet<BHPayment>>(gqlFields: SelSet) =>
      watchEntitiesByIDs(BHPayment, ids, gqlFields),
});

export const getAllBHPayments = registerQuery({
  operationName: 'getAllBHPayments',
  type: () => [BHPayment],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<BHPayment>>(gqlFields: SelSet) =>
      readAllEntities(BHPayment, gqlFields, { skip, limit, excludeIDs }),
});

export const subscribeAllBHPayments = registerSubscription({
  operationName: 'subscribeAllBHPayments',
  type: () => [BHPayment],
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'excludeIDs', type: () => [ID] },
  ],
  resolve:
    (skip?: number, limit?: number, excludeIDs?: string[]) =>
    <SelSet extends BaseQLSelectionSet<BHPayment>>(gqlFields: SelSet) =>
      watchAllEntities(BHPayment, gqlFields, { skip, limit, excludeIDs }),
});

export const getBHPaymentByID =
  (id: string | null | undefined) =>
  async <SelSet extends BaseQLSelectionSet<BHPayment>>(gqlFields: SelSet) => {
    if (!id) return null;
    return (await getBHPaymentsByIDs([id])(gqlFields))[0] ?? null;
  };

export const subscribeBHPaymentByID =
  (id: string | null | undefined) =>
  <SelSet extends BaseQLSelectionSet<BHPayment>>(gqlFields: SelSet) =>
    id ? subscribeBHPaymentsByIDs([id])(gqlFields).pipe(map(entities => entities[0] ?? null)) : of(null);
